import { type TemplateResult, nothing } from 'lit-html';

import { array } from '@disy/lib/array-util';
import type { Nullable } from '@disy/lib/type-util';
import type { ChildDirectiveResult } from '@disy/ui/lit-util';

// These two are only building blocks for the types exported below and must not be exported themselves.
// It would defeat the purpose of the Content and LitContent types, which is standardization.
type BasicContent = string | Node;
type BasicLitContent = TemplateResult | ChildDirectiveResult | BasicContent;

/**
 * Type that is commonly used in the API of components that allow customizing their content.
 *
 * For convenience, we allow `null` and `undefined` values. Use {@link normalizeContent}
 * to normalize the content for use with the DOM.
 */
export type Content = BasicContent | Nullable<BasicContent>[];

/**
 * Type that is commonly used in the API of components that allow customizing their content.
 *
 * For convenience, we allow `null` and `undefined` and Lit's `nothing`, which Lit ignores.
 *
 * _Note:_ This is a super-type of {@link Content}, i.e. you don't need to support both in the same component.
 */
export type LitContent = BasicLitContent | (Nullable<BasicLitContent> | typeof nothing)[];

export function isContent(value: unknown): value is Content {
  return isStringOrNode(value) || (Array.isArray(value) && value.every(isNullableStringOrNode));
}

function isNullableStringOrNode(value: unknown): value is string | Node {
  return value == null || isStringOrNode(value);
}

function isStringOrNode(value: unknown): value is string | Node {
  return typeof value === 'string' || value instanceof Node;
}

export function normalizeContent(content: Nullable<Content>): BasicContent[];
export function normalizeContent(content: Nullable<LitContent>): BasicLitContent[];
export function normalizeContent(content: Nullable<LitContent>) {
  return array(content).filter((c) => c !== nothing && !!c);
}
